if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.sellingPlans = this.querySelector('selling-plans');
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer'))
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
      }

      connectedCallback() {
        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );
        this.selectedVariant = this.productJson.variants.find(
          (variant) => String(variant.id) === this.variantIdInput.value,
        );
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        this.selectedVariant = this.productJson.variants.find(
          (variant) =>
            JSON.stringify(variant.options) ===
            JSON.stringify(selectedOptionValues),
        );
        this.changeVariantInputId();
        this.updateSubmitButton();
        this.updateURL();
        this.updateFormatCardImages();

        publish(PUB_SUB_EVENTS.variantChange, {
          data: { variantId: this.selectedVariant.id },
        });
      }

      // Format cards show the variant image for "this format + the currently
      // selected other options", so a flavor change swaps the card photos.
      updateFormatCardImages() {
        const cards = this.querySelectorAll('[data-format-card]');

        if (!cards.length || !this.selectedVariant) return;

        cards.forEach((card) => {
          const position = Number(card.dataset.optionPosition) - 1;
          const target = [...this.selectedVariant.options];
          target[position] = card.dataset.optionValue;

          const match = this.productJson.variants.find(
            (variant) =>
              JSON.stringify(variant.options) === JSON.stringify(target),
          );
          const img = card.querySelector('[data-format-card-image] img');

          if (!match || !match.featured_image || !img) return;
          if (img.dataset.cardSrc === match.featured_image.src) return;

          img.dataset.cardSrc = match.featured_image.src;
          img.src = match.featured_image.src + '&width=720';
          img.removeAttribute('srcset');
        });
      }

      changeVariantInputId() {
        this.form.querySelector('[name=id]').value = this.selectedVariant.id;
      }

      updateURL() {
        if (!this.selectedVariant || this.dataset.updateUrl !== 'true') return;

        const url = new URL(window.location.href);
        url.searchParams.set('variant', this.selectedVariant.id);
        window.history.replaceState({}, '', url);
      }

      updateSubmitButton() {
        if (!this.selectedVariant?.available) {
          this.toggleSubmitButton(true, window.variantStrings.soldOut);

          return;
        }

        this.toggleSubmitButton(false);
      }

      onSubmitHandler(event) {
        event.preventDefault();

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id),
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });

              this.error = true;

              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker(
              'add:wait-for-subscribers',
            );
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker(
                  'add:wait-for-subscribers',
                  startMarker,
                );
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure(
                      'add:paint-updated-sections',
                      () => {
                        this.cart.renderContents(response);
                      },
                    );
                  });
                },
                { once: true },
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty'))
              this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', event);
          });
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          const variantPrice = this.variantPricesJson.find(
            (variant) => variant.id === this.selectedVariant.id,
          );
          const priceHtml =
            variantPrice.compare_at_price.value > variantPrice.price.value
              ? `<ins>${variantPrice.price.html}</ins> <del>${variantPrice.compare_at_price.html}</del>`
              : variantPrice.price.html;
          const sellingPlanPriceHtml = variantPrice.selling_plans?.find(
            (sellingPlan) =>
              String(sellingPlan.id) ===
              this.sellingPlans?.querySelector(
                '[name="selling_plan"]:not(:disabled)',
              )?.value,
          )?.price.html;

          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.innerHTML =
            window.variantStrings.addToCart +
            ' - ' +
            (sellingPlanPriceHtml || priceHtml);
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      get productJson() {
        return JSON.parse(
          this.querySelector('[data-product-json]').textContent,
        );
      }

      get variantPricesJson() {
        return JSON.parse(
          this.querySelector('[data-variant-prices]').textContent,
        );
      }
    },
  );
}
